import { useEffect, useState } from 'react';
import { PlusIcon, Cog6ToothIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { catalogApi, CatalogSummary, CatalogType } from '@/services/api';

// Varios catálogos por organización (VIDA-370): selector del catálogo con el
// que se trabaja, alta de uno nuevo y su ficha (paso 1 del procedimiento:
// tipo, al menos 5 objetivos y el marco del storytelling).

const MIN_OBJECTIVES = 5;
const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;

interface FormProps {
  catalog: CatalogSummary | null; // null = nuevo
  types: CatalogType[];
  onClose: () => void;
  onSaved: (catalog: CatalogSummary) => void;
}

function CatalogFormModal({ catalog, types, onClose, onSaved }: FormProps) {
  const [name, setName] = useState(catalog?.name ?? '');
  const [type, setType] = useState(catalog?.type ?? '');
  const [objectives, setObjectives] = useState<string[]>(() => {
    const list = [...(catalog?.objectives ?? [])];
    while (list.length < MIN_OBJECTIVES) list.push('');
    return list;
  });
  const [storytelling, setStorytelling] = useState(catalog?.storytelling ?? '');
  const [status, setStatus] = useState<'draft' | 'active'>(catalog?.status ?? 'draft');
  const [saving, setSaving] = useState(false);

  const filled = objectives.filter((o) => o.trim()).length;
  const isPrimary = !!catalog?.is_primary;

  const save = async () => {
    if (!name.trim()) return toast.error('Ponle un nombre al catálogo');
    if (filled < MIN_OBJECTIVES) return toast.error(`Indica al menos ${MIN_OBJECTIVES} objetivos`);
    setSaving(true);
    try {
      const data = { name: name.trim(), type, objectives: objectives.map((o) => o.trim()).filter(Boolean), storytelling };
      const res = catalog
        ? await catalogApi.updateCatalog(catalog._id ?? 'principal', isPrimary ? data : { ...data, status })
        : await catalogApi.createCatalog({ ...data, status });
      toast.success(catalog ? 'Catálogo guardado' : 'Catálogo creado');
      onSaved(res.data.catalog);
    } catch (err) {
      toast.error(errorMessage(err, 'No se ha podido guardar el catálogo'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{catalog ? `Ficha de «${catalog.name}»` : 'Nuevo catálogo'}</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Cerrar">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label className={labelClass}>Nombre</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Catálogo de mercados" />
            {catalog && !isPrimary && <p className="mt-1 text-xs text-gray-500">Nombre corto en la web: <code>{catalog.slug}</code></p>}
          </div>

          <div>
            <label className={labelClass}>Tipo de catálogo</label>
            {types.length > 0 ? (
              <select value={type} onChange={(e) => setType(e.target.value)} className={inputClass}>
                <option value="">Sin tipo</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            ) : (
              <p className="text-xs text-gray-500">El árbol «Tipos de catálogo» todavía no está cargado.</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Objetivos <span className="text-gray-400 font-normal">(al menos {MIN_OBJECTIVES}; llevas {filled})</span></label>
            <div className="space-y-2">
              {objectives.map((o, i) => (
                <input
                  key={i}
                  value={o}
                  onChange={(e) => setObjectives((list) => list.map((x, j) => (j === i ? e.target.value : x)))}
                  className={inputClass}
                  placeholder={`Objetivo ${i + 1}`}
                />
              ))}
            </div>
            <button type="button" onClick={() => setObjectives((list) => [...list, ''])} className="mt-2 text-sm text-vida-600 hover:text-vida-700">
              + Añadir objetivo
            </button>
          </div>

          <div>
            <label className={labelClass}>Storytelling</label>
            <textarea value={storytelling} onChange={(e) => setStorytelling(e.target.value)} rows={4} className={inputClass}
              placeholder="La historia que cuenta este catálogo y a quién va dirigido" />
          </div>

          {isPrimary ? (
            <p className="text-xs text-gray-500">El catálogo principal siempre está publicado: es el que ya usa tu web.</p>
          ) : (
            <div>
              <label className={labelClass}>Estado</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as 'draft' | 'active')} className={inputClass}>
                <option value="draft">Borrador (su web todavía no responde)</option>
                <option value="active">Publicado</option>
              </select>
              {catalog && (
                <p className="mt-1 text-xs text-gray-500">
                  Su web se publica en Vercel desde la plantilla con la variable <code>NEXT_PUBLIC_CATALOG_SLUG={catalog.slug}</code>.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={save} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-vida-600 rounded-lg hover:bg-vida-700 disabled:opacity-50">
            {saving ? 'Guardando…' : catalog ? 'Guardar' : 'Crear catálogo'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface SwitcherProps {
  value: string | null; // id del catálogo adicional; null = principal
  onChange: (id: string | null) => void;
  canEdit: boolean;
}

export default function CatalogSwitcher({ value, onChange, canEdit }: SwitcherProps) {
  const [catalogs, setCatalogs] = useState<CatalogSummary[]>([]);
  const [types, setTypes] = useState<CatalogType[]>([]);
  const [editing, setEditing] = useState<CatalogSummary | 'new' | null>(null);

  const load = async () => {
    try {
      const res = await catalogApi.listCatalogs();
      setCatalogs(res.data.catalogs || []);
    } catch {
      setCatalogs([]);
    }
  };

  useEffect(() => {
    load();
    catalogApi.catalogTypes().then((r) => setTypes(r.data.types || [])).catch(() => setTypes([]));
  }, []);

  const current = catalogs.find((c) => (c.is_primary ? value === null : c._id === value)) || catalogs[0];
  const typeName = types.find((t) => t.id === current?.type)?.name;

  const remove = async () => {
    if (!current || current.is_primary || !current._id) return;
    if (!window.confirm(`¿Borrar el catálogo «${current.name}» y su web? Los enlaces no se borran: solo dejan de estar en este catálogo.`)) return;
    try {
      await catalogApi.deleteCatalog(current._id);
      toast.success('Catálogo borrado');
      onChange(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, 'No se ha podido borrar el catálogo'));
    }
  };

  // Con un solo catálogo y sin permiso de edición no hay nada que elegir.
  if (catalogs.length <= 1 && !canEdit) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
      <label className="text-sm font-medium text-gray-700" htmlFor="catalog-switcher">Catálogo</label>
      <select
        id="catalog-switcher"
        value={current?.is_primary ? '' : current?._id ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500 min-w-[220px]"
      >
        {catalogs.map((c) => (
          <option key={c._id ?? 'principal'} value={c.is_primary ? '' : c._id ?? ''}>
            {c.name}{c.is_primary ? ' (principal)' : c.status === 'draft' ? ' · borrador' : ''}
          </option>
        ))}
      </select>
      {typeName && <span className="text-xs text-gray-500">{typeName}</span>}
      {canEdit && (
        <div className="flex items-center gap-2 ml-auto">
          {current && (
            <button onClick={() => setEditing(current)} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Cog6ToothIcon className="w-4 h-4" /> Ficha
            </button>
          )}
          {current && !current.is_primary && (
            <button onClick={remove} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50">
              <TrashIcon className="w-4 h-4" /> Borrar
            </button>
          )}
          <button onClick={() => setEditing('new')} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-vida-700 border border-vida-200 rounded-lg hover:bg-vida-50">
            <PlusIcon className="w-4 h-4" /> Nuevo catálogo
          </button>
        </div>
      )}

      {editing && (
        <CatalogFormModal
          catalog={editing === 'new' ? null : editing}
          types={types}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            load();
            if (editing === 'new') onChange(saved._id);
          }}
        />
      )}
    </div>
  );
}
